import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';
import {
  DocQueryDocumentTypeOption,
  DocQueryScope,
  DocQuerySettings,
  DocQuerySettingsRequest,
  DocQuerySettingsResponse,
  DocQuerySettingsValues
} from '../models/doc-query-config.model';

const SCOPE_TO_OPTION: Record<DocQueryScope, DocQueryDocumentTypeOption> = {
  [DocQueryScope.None]: 'Ninguno',
  [DocQueryScope.All]: 'Todos',
  [DocQueryScope.Paychecks]: 'Recibos'
};
const OPTION_TO_SCOPE: Record<DocQueryDocumentTypeOption, DocQueryScope> = {
  Ninguno: DocQueryScope.None,
  Todos: DocQueryScope.All,
  Recibos: DocQueryScope.Paychecks
};

/** Recibos y liquidaciones se reconocen por el nombre del tipo de documentación. */
export function isPaycheckDocumentType(documentationTypeName: string | null | undefined): boolean {
  const name = documentationTypeName?.toLowerCase() ?? '';
  return name.includes('recibos') || name.includes('de haberes');
}

/** Indica si el alcance configurado habilita consultas para ese tipo de documentación. */
export function isQueryAllowedForDocument(
  scope: DocQueryScope | null | undefined,
  documentationTypeName: string | null | undefined
): boolean {
  switch (scope) {
    case DocQueryScope.All:
      return true;
    case DocQueryScope.Paychecks:
      return isPaycheckDocumentType(documentationTypeName);
    default:
      return false;
  }
}

/**
 * Configuración de consultas de empleados por empresa (alcance, destinatarios y motivos).
 * API cpp `/DocMessages/Settings`.
 */
@Injectable({
  providedIn: 'root'
})
export class DocQueryConfigService {
  private readonly http = inject(HttpClient);

  private readonly settingsUrl = `${AppConfig.settings.apiUrls.cpp}/DocMessages/Settings`;

  /** Configuración por empresa para los visores del empleado, que la consultan al abrir cada documento. */
  private readonly settingsCache = new Map<number, Observable<DocQuerySettings | null>>();

  /**
   * Indica si el empleado puede hacer consultas sobre un documento según el alcance de su empresa.
   * Sin configuración o si no se puede obtener, no se habilitan.
   */
  canQueryDocument(ouId: number | null | undefined, documentationTypeName: string | null | undefined): Observable<boolean> {
    if (!ouId) {
      return of(false);
    }
    return this.getCachedSettings(ouId).pipe(
      map(settings => isQueryAllowedForDocument(settings?.scope, documentationTypeName)),
      catchError(() => of(false))
    );
  }

  private getCachedSettings(ouId: number): Observable<DocQuerySettings | null> {
    let settings$ = this.settingsCache.get(ouId);
    if (!settings$) {
      settings$ = this.getSettings(ouId).pipe(
        // Un error no queda cacheado: el próximo documento vuelve a intentar
        catchError(err => {
          this.settingsCache.delete(ouId);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.settingsCache.set(ouId, settings$);
    }
    return settings$;
  }

  /**
   * Devuelve la configuración de la empresa o `null` si aún no existe (404).
   * Con `ouId` la API responde la configuración de esa empresa; sin él, la de todas.
   */
  getSettings(ouId: number): Observable<DocQuerySettings | null> {
    const params = new HttpParams().set('ouId', ouId);
    return this.http.get<DocQuerySettingsResponse | null>(this.settingsUrl, { params }).pipe(
      map(res => (res ? this.fromResponse(res) : null)),
      catchError(err => (this.isNotFound(err) ? of(null) : throwError(() => err)))
    );
  }

  /** Crea la configuración si no existe (`id` vacío) o la actualiza, también para el alcance "Ninguno". */
  saveSettings(id: string | null, ouId: number, values: DocQuerySettingsValues): Observable<DocQuerySettings> {
    const request: DocQuerySettingsRequest = {
      ouId,
      notifyEmails: values.notifyEmails,
      motives: values.motives,
      documentTypeOption: SCOPE_TO_OPTION[values.scope]
    };
    const save$ = id
      ? this.http.put<DocQuerySettingsResponse>(`${this.settingsUrl}/${id}`, request)
      : this.http.post<DocQuerySettingsResponse>(this.settingsUrl, request);

    return save$.pipe(
      map(res => this.fromResponse(res)),
      tap(() => this.settingsCache.delete(ouId))
    );
  }

  private fromResponse(res: DocQuerySettingsResponse): DocQuerySettings {
    return {
      id: res.id,
      ouId: res.ouId,
      scope: OPTION_TO_SCOPE[res.documentTypeOption],
      notifyEmails: res.notifyEmails ?? [],
      motives: res.motives ?? []
    };
  }

  // El TokenInterceptor convierte los errores HTTP a { code: '_<status>' }
  private isNotFound(err: unknown): boolean {
    if (err instanceof HttpErrorResponse) {
      return err.status === HttpStatusCode.NotFound;
    }
    return (err as { code?: string })?.code === `_${HttpStatusCode.NotFound}`;
  }
}
